<template>
  <section v-if="loading || hasData">
    <page-sub-header title="Find & Combine" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <date-range-picker
          :from="dateFrom"
          :to="dateTo"
          :earliest="earliestAvailable"
          @update:from="dateFrom = $event"
          @update:to="dateTo = $event"
        />
      </template>
    </page-sub-header>

    <sme-card v-if="loading">
      <app-loading />
    </sme-card>

    <template v-else>
      <!-- Reach & engagement -->
      <chart-with-companion-kpis
        v-if="hasSignupsData"
        data-pdf-section="signups"
        card-class="mb-4"
        chart-title="Pension signups over time"
        chart-subtitle="Employees with an approved personal pension application"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedSignups"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedSignups.name } } },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="totalSignups"
            :formatter="toInteger"
            description="Signups"
            class="mb-3"
            background-color="core-1-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <app-stat
            :stat="signupRate"
            :formatter="toNumber"
            suffix="%"
            description="Signup rate"
            class="mb-3"
            background-color="core-1-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastSignups) }} employees were approved for a personal pension.
            {{ signupsPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <!-- Found -->
      <sme-card v-if="hasFoundData" data-pdf-section="found" class="mb-4" title="Pensions found" title-tag="h3">
        <p class="text-muted">All-time totals of pension pots found for members</p>
        <b-row>
          <b-col md="6" class="mb-3 mb-md-0">
            <app-stat
              :stat="totalPotsFound"
              :formatter="toInteger"
              description="Pots found"
              background-color="core-5-lighten-90"
              color="default"
              pdf-kpi
              delta-neutral
            />
          </b-col>
          <b-col md="6">
            <app-stat
              :stat="totalValueFound"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Total value found"
              background-color="core-5-lighten-90"
              color="default"
              pdf-kpi
              delta-neutral
            />
          </b-col>
        </b-row>
      </sme-card>

      <!-- Combined -->
      <sme-card
        v-if="hasCombinedData"
        data-pdf-section="combined"
        class="mb-4"
        title="Pensions combined"
        title-tag="h3"
      >
        <p class="text-muted">All-time totals of completed pension consolidations</p>
        <b-row>
          <b-col md="6" class="mb-3 mb-md-0">
            <app-stat
              :stat="totalPotsCombined"
              :formatter="toInteger"
              description="Consolidations completed"
              background-color="core-4-lighten-90"
              color="default"
              pdf-kpi
              delta-neutral
            />
          </b-col>
          <b-col md="6">
            <app-stat
              :stat="totalValueCombined"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Value consolidated"
              background-color="core-4-lighten-90"
              color="default"
              pdf-kpi
              delta-neutral
            />
          </b-col>
        </b-row>
      </sme-card>
    </template>
  </section>
</template>

<script>
import { markerSection } from '@/utils/analyticsSections';

// Single source of truth for PensionMetrics' subsections, imported by both the
// Find & Combine tab's `sectionSpecs` and the homepage "Download all" capture.
export function pensionMetricsSections(pensionRef) {
  return [
    markerSection(pensionRef, {
      key: 'signups',
      label: 'Signups',
      hasDataKey: 'hasSignupsData',
      exportTableKey: 'getSignupsExportTable',
    }),
    markerSection(pensionRef, {
      key: 'found',
      label: 'Found',
      hasDataKey: 'hasFoundData',
      exportTableKey: 'getFoundExportTable',
    }),
    markerSection(pensionRef, {
      key: 'combined',
      label: 'Combined',
      hasDataKey: 'hasCombinedData',
      exportTableKey: 'getCombinedExportTable',
    }),
  ];
}
</script>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import State from '@/state/State';
import useDateRangeSelection from '@/state/composables/useDateRangeSelection';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { getInitialAspectRatio } from '@/utils/chart';
import { toInteger, toNumber, currencySymbolFromCode } from '@/utils/common';

const { divisionId } = useDivisionSelection();
const { from: dateFrom, to: dateTo, apiFrom, apiTo } = useDateRangeSelection();

const loading = ref(true);
const earliestAvailable = ref(null);
const timeDataset = ref(null);
const enrolledDataset = ref(null);
const signupsDataset = ref(null);
// All-time Found/Combined figures — unaffected by the date range by design.
const totals = ref(null);

// No grain toggle: timeSeries stays on the composable's monthly default.
const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const currencySymbol = computed(() => currencySymbolFromCode(State.state.company.default_currency));

const timeGroupedSignups = computed(() => datasetToTimeGroupedDataset(signupsDataset));
const timeGroupedEnrolled = computed(() => datasetToTimeGroupedDataset(enrolledDataset));

const sumValues = dataset => (dataset?.values || []).reduce((total, value) => total + (value || 0), 0);
const lastValue = dataset => {
  const values = dataset?.values;
  return values?.length ? values[values.length - 1] : 0;
};

// The signups KPI is cumulative across the visible (complete) periods; the
// "last" helpers drive the per-period sentence beneath the chart.
const totalSignups = computed(() => sumValues(timeGroupedSignups.value));
const lastSignups = computed(() => lastValue(timeGroupedSignups.value));
const signupsPrevDiffText = computed(() => getPrevDiffText(timeGroupedSignups, toInteger));
const signupRate = computed(() => {
  const enrolled = lastValue(timeGroupedEnrolled.value);
  return enrolled ? Number(toNumber((totalSignups.value / enrolled) * 100)) : 0;
});

const totalPotsFound = computed(() => totals.value?.pots_found || 0);
const totalValueFound = computed(() => totals.value?.pots_found_value || 0);
const totalPotsCombined = computed(() => totals.value?.pots_combined || 0);
const totalValueCombined = computed(() => totals.value?.pots_combined_value || 0);

watch([divisionId, dateFrom, dateTo], () => getMetrics());

const getMetrics = async () => {
  loading.value = true;

  const {
    results,
    earliest_available,
    totals: apiTotals,
  } = await ApiClient.getPensionFindCombineMetrics(timeSeries.value, divisionId.value, apiFrom.value, apiTo.value);

  earliestAvailable.value = earliest_available ?? null;
  totals.value = apiTotals ?? null;

  if (results?.length) {
    const byId = id => results.find(result => result.id === id);

    // A series missing from `results` must stay null so the time-group helpers
    // skip it; spreading `undefined` yields a `values`-less object that throws
    // when datasetToTimeGroupedDataset slices it.
    const signups = byId('signups');
    timeDataset.value = byId('time');
    enrolledDataset.value = byId('enrolled');
    signupsDataset.value = signups ? { ...signups, name: 'Signups', lineColor: PaletteColors['core-1'] } : null;
  }

  loading.value = false;
};

onBeforeMount(() => getMetrics());

const hasSignupsData = computed(() => timeGroupedSignups.value?.values?.length > 0);
const hasFoundData = computed(() => totalPotsFound.value > 0);
const hasCombinedData = computed(() => totalPotsCombined.value > 0);

const hasData = computed(() => !loading.value && (hasSignupsData.value || hasFoundData.value || hasCombinedData.value));
const isDataReady = computed(() => hasData.value && !loading.value);

function getSignupsExportTable() {
  if (!hasSignupsData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Signups'],
    rows: timeGroupDataset.value.values.map((time, index) => [time, timeGroupedSignups.value?.values[index]]),
  };
}

function getFoundExportTable() {
  if (!hasFoundData.value) {
    return null;
  }
  return {
    headers: ['Pots found', 'Total value found'],
    rows: [[totalPotsFound.value, Math.round(totalValueFound.value)]],
  };
}

function getCombinedExportTable() {
  if (!hasCombinedData.value) {
    return null;
  }
  return {
    headers: ['Consolidations completed', 'Value consolidated'],
    rows: [[totalPotsCombined.value, Math.round(totalValueCombined.value)]],
  };
}

defineExpose({
  hasData,
  hasSignupsData,
  hasFoundData,
  hasCombinedData,
  getSignupsExportTable,
  getFoundExportTable,
  getCombinedExportTable,
});
</script>
